import { TrackedChangeType } from 'prose-core'
import { TextSelection } from 'prosemirror-state'

import { ourUuid } from 'utils'

import { stepTracker } from '../stepTracker'
import { isDeletion, isEmptyInsertionParagraph, isHeading, isInsertion, isParagraph } from '../utils'
import { type TrackedChangesCommandHandler } from './types'
import { createTrackedChange, fastHeuristicSearch } from './utils'

export const joinBackward: TrackedChangesCommandHandler = ({ originalTr, wrappedTr, map, state, userId }) => {
  const currentNode = state.doc.nodeAt(state.selection.from)
  const currentResolved = state.doc.resolve(state.selection.from)
  if (currentNode?.isText && currentResolved.textOffset === 0) {
    const nodeBefore = state.doc.nodeAt(state.selection.$from.before(state.selection.$from.depth) - 2)
    // Node before is an insertion, the default action is to remove it, in this case let it do that
    if (nodeBefore && isEmptyInsertionParagraph(nodeBefore)) {
      originalTr.steps.map((step) => wrappedTr.step(step))
      return true
    }
  }

  const { status, match, stack } = fastHeuristicSearch(wrappedTr.doc.content, ({ node, from, to }) => {
    // This node starts after the selection, somehow we missed ourselves, stop searching
    if (from > state.selection.from) {
      return 'stop'
    }
    // Found ourselves, stop searching
    if (from === state.selection.from) {
      return 'found'
    }
    // We found a possible target
    if (!isDeletion(node) && (node.isText || isParagraph(node) || isHeading(node))) {
      return 'stack'
    }
    // Deleted inlines like smarkLink don't tag their text and are a pitfall
    if (isDeletion(node) && node.isInline) {
      return 'skip'
    }
    // Check inside for a place to move the content into
    return 'search'
  })

  // We can delete and pull into
  const parent = match?.parentList.at(-1)
  const target = stack.filter((stackMatch) => stackMatch.from !== parent?.from).at(-1)
  if (status === 'found' && match?.node.isText && parent && target) {
    if (isDeletion(match.node)) {
      // Silence the action
      return true
    }
    if (target) {
      const changeId = ourUuid()
      const insertionTrackedChange = createTrackedChange(TrackedChangeType.Insertion, userId, changeId)
      const deletionTrackedChange = createTrackedChange(TrackedChangeType.Deletion, userId, changeId)
      const targetTo = target.node.isText ? target.to : target.to - 1

      let sourceStart = match.from
      parent.node.content.content.forEach((node) => {
        if (isDeletion(node)) {
          sourceStart += node.nodeSize
          return
        }
        const nodeSize = node.isText ? node.textContent.length : node.nodeSize
        // We want to mark the node as deleted, but not if it was an insertion
        if (!isInsertion(node)) {
          if (node.isText) {
            wrappedTr.addMark(
              wrappedTr.mapping.map(sourceStart),
              wrappedTr.mapping.map(sourceStart + nodeSize),
              state.schema.marks.deletion.create(deletionTrackedChange),
            )
          } else {
            wrappedTr.setNodeMarkup(
              wrappedTr.mapping.map(sourceStart),
              node.type,
              { ...node.attrs, trackedChanges: [deletionTrackedChange] },
              node.marks,
            )
          }
        } else {
          // Actually remove insertions in this case
          wrappedTr.replace(wrappedTr.mapping.map(sourceStart), wrappedTr.mapping.map(sourceStart + nodeSize))
        }
        const copy = node.isText
          ? state.schema.text(node.textContent, [
              ...node.marks,
              state.schema.marks.insertion.create(insertionTrackedChange),
            ])
          : node.copy()
        if (!node.isText) {
          copy.attrs.trackedChanges = [insertionTrackedChange]
        }
        wrappedTr.insert(wrappedTr.mapping.map(targetTo), copy)
        sourceStart += nodeSize
        return
      })
      if (!isDeletion(parent.node)) {
        wrappedTr.setNodeMarkup(
          wrappedTr.mapping.map(parent.from),
          parent.node.type,
          { ...parent.node.attrs, trackedChanges: [deletionTrackedChange] },
          parent.node.marks,
        )
      }

      originalTr.setMeta('preservedSelectionForTrackedChanges', TextSelection.create(wrappedTr.doc, targetTo))

      return true
    } else {
      // Do nothing, we are at the effective top of the doc
      return true
    }
  }

  stepTracker({ originalTr, wrappedTr, map, state, userId })
  return true
}
